// src/features/files/hooks/useFileDragDrop.js
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  pointerWithin,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { filesAPI } from '../services/files';
import { foldersAPI } from '../services/folders';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { isAppFolder, isOwnItem } from '../utils/getFolderName';

const HOVER_OPEN_DELAY_MS = 1500;

// Drop zone ID helpers
export const folderDropId = (folderId) => `folder:${folderId}`;
export const breadcrumbDropId = (crumbId) => `breadcrumb:${crumbId ?? 'root'}`;

const parseDropId = (dropId) => {
  if (!dropId) return null;
  const [type, id] = dropId.split(':');
  return { type, id: id === 'root' ? null : id };
};

export function useFileDragDrop({ onRefresh, onFolderOpen, canDrag = false, canDragFolder, canDragFile }) {
  const { t } = useTranslation(['files']);
  const { showError } = useSnackbar();
  const [activeItem, setActiveItem] = useState(null);
  const [overId, setOverId] = useState(null);

  const hoverTimerRef = useRef(null);
  const hoverFolderRef = useRef(undefined); // undefined = nothing hovered; null = root; uuid = folder
  const hoverOpenedFolderRef = useRef(null); // null = not set; string = full drop zone ID (e.g. 'breadcrumb:root')
  const activeDragDataRef = useRef(null);    // snapshot at drag start; survives component unmount

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    hoverFolderRef.current = undefined;
  }, []);

  const handleDragStart = useCallback(({ active }) => {
    const data = active.data.current ?? null;
    setActiveItem(data);
    activeDragDataRef.current = data;
    clearHoverTimer();
    hoverOpenedFolderRef.current = null;
  }, [clearHoverTimer]);

  const handleDragOver = useCallback(({ over }) => {
    setOverId(over?.id ?? null);

    if (!over) {
      clearHoverTimer();
      return;
    }

    const target = parseDropId(over.id);
    if (!target) {
      clearHoverTimer();
      return;
    }

    // Auto-open folders and breadcrumb ancestors on hover
    if ((target.type === 'folder' || target.type === 'breadcrumb') && target.id !== hoverFolderRef.current) {
      clearHoverTimer();
      hoverFolderRef.current = target.id;
      const capturedDropId = over.id;
      hoverTimerRef.current = setTimeout(() => {
        hoverOpenedFolderRef.current = capturedDropId; // store full drop zone ID, not parsed target.id
        onFolderOpen?.(target.id);
        clearHoverTimer();
      }, HOVER_OPEN_DELAY_MS);
    }
  }, [clearHoverTimer, onFolderOpen]);

  const handleDragEnd = useCallback(async ({ over }) => {
    setActiveItem(null);
    setOverId(null);
    clearHoverTimer();

    // Use snapshotted data — active.data.current may be cleared if component unmounted during hover-to-open
    const dragged = activeDragDataRef.current;
    activeDragDataRef.current = null;
    if (!dragged?.type || !dragged?.item) return;

    // If dropped with no target, fall back to the drop zone opened via hover-to-open
    const resolvedOverId = over?.id ?? hoverOpenedFolderRef.current;
    hoverOpenedFolderRef.current = null;

    if (!resolvedOverId) return;

    const target = parseDropId(resolvedOverId);
    if (!target) return;

    const targetFolderId = target.id; // null = root

    // Skip no-op for folders (FileListItemDTO doesn't expose folder_id so we skip for files)
    if (dragged.type === 'folder') {
      const currentParentId = dragged.item.parent_folder_id ?? null;
      if (targetFolderId === currentParentId) return;
      // Skip dropping a folder onto itself
      if (dragged.item.folder_id === targetFolderId) return;
    }

    try {
      if (dragged.type === 'file') {
        await filesAPI.moveFile(dragged.item.file_id, targetFolderId);
      } else {
        await foldersAPI.moveFolder(dragged.item.folder_id, targetFolderId);
      }
      onRefresh?.();
    } catch (err) {
      showError(err.response?.data?.message || t('files:errors.move_failed'));
    }
  }, [clearHoverTimer, onRefresh, showError, t]);

  const handleDragCancel = useCallback(() => {
    setActiveItem(null);
    setOverId(null);
    clearHoverTimer();
    hoverOpenedFolderRef.current = null;
  }, [clearHoverTimer]);

  const dndContextProps = {
    sensors,
    collisionDetection: pointerWithin,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel,
  };

  // Returns props to spread onto a draggable element.
  // Called per-item by the view components via useDraggable directly —
  // this hook exposes the item data shape and helpers instead.
  // App folders (Udstyr, Opgaver …) stay where the app looks for them, and what
  // another department shares stays where it put it
  const getDragData = useCallback((item, type) => ({
    id: type === 'file' ? `file:${item.file_id}` : `folder:${item.folder_id}`,
    data: { type, item },
    disabled: !canDrag || !isOwnItem(item) || (type === 'folder'
      ? isAppFolder(item) || canDragFolder?.(item) === false
      : canDragFile?.(item) === false),
  }), [canDrag, canDragFolder, canDragFile]);

  const isOver = useCallback((dropId) => overId === dropId, [overId]);

  return {
    dndContextProps,
    getDragData,
    isOver,
    activeItem,
    overId,
  };
}
